'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, X } from 'lucide-react';
import customFetch, { researchFetch } from '@/lib/utils/fetch';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useTranslation } from '@/lib/i18n';
import { useChatMode } from '@/contexts/ChatModeContext';
import { usePathname } from 'next/navigation';
import Share2X from './Share2X';
import Card from '../common/Card';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
  chatTitle?: string;
  query: string;
  messageIndex?: number;
}

export default function ShareModal({
  isOpen,
  onClose,
  chatId,
  chatTitle,
  query,
  messageIndex,
}: ShareModalProps) {
  const [shareUrl, setShareUrl] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [currentMessageCount, setCurrentMessageCount] = useState(0);
  const [sharedMessageCount, setSharedMessageCount] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const { mode } = useChatMode();
  const pathname = usePathname();

  const { showSuccess } = useSnackbar();
  const { t } = useTranslation();

  const fetcher = useMemo(
    () => (['alfa', 'predic'].includes(mode) ? customFetch : researchFetch),
    [mode],
  );
  const fetcherUrl =
    mode === 'alfa'
      ? `/api/share/${chatId}`
      : mode === 'predic'
        ? `/api/predic/share/${chatId}`
        : `api/conversations/${chatId}/share`;
  const postFetcherUrl = ['alfa', 'predic'].includes(mode)
    ? `/api/share`
    : `api/conversations/${chatId}/share`;

  useEffect(() => {
    if (isOpen) {
      const query = pathname.startsWith('/r/') ? '?is_research=1' : '';
      // 始终显示公开链接格式，不管当前是否公开，点击复制时会自动转换为公开链接
      const publicUrl = `${window.location.origin}/${pathname.startsWith('/notion') ? 'notion' : pathname.startsWith('/predic') ? 'predic/share' : 'share'}/${chatId}${query}`;
      setShareUrl(publicUrl);

      // 检查是否已公开（仅用于内部状态）
      checkShareStatus();
    }
  }, [isOpen, chatId, mode]);

  // 处理 ESC 键关闭
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const checkShareStatus = async () => {
    try {
      // 检查会话是否已公开（通过尝试获取分享内容）
      const response = await fetcher(fetcherUrl);
      if (response.ok) {
        const data = await response.json();
        // 如果能成功获取，说明已公开
        setIsPublic(true);
        setSharedMessageCount(data.messageCount || 0);
        setCurrentMessageCount(data.totalMessages || 0);
        // 检查是否有新消息
        setHasNewMessages(data.totalMessages > data.messageCount);
      } else {
        // 如果返回403或404，说明未公开
        setIsPublic(false);
        setHasNewMessages(false);
      }
    } catch (error) {
      // 出错时默认为未公开
      setIsPublic(false);
      setHasNewMessages(false);
    }
  };

  const handleCopy = async () => {
    if (!isPublic || hasNewMessages || mode !== 'alfa') {
      // 第一次点击或更新分享：将链接转为公开
      setIsLoading(true);
      try {
        const response = await fetcher(postFetcherUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId,
            makePublic: true,
            updateShare: currentMessageCount,
            messageIndex,
            searchQueryId: chatId,
          }),
        });
        const data = await response.json();
        setIsPublic(true);
        setSharedMessageCount(data.messageCount);
        setHasNewMessages(false); // 更新后没有新消息了

        // 复制到剪贴板（链接已经在显示了，直接复制）
        await navigator.clipboard.writeText(shareUrl);
        setIsCopied(true);
        showSuccess(
          hasNewMessages ? t('Share updated and link copied') : t('Link copied to clipboard'),
        );
        setTimeout(() => setIsCopied(false), 2000);
      } catch (error) {
        console.error('Failed to make chat public:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // 已经是公开链接，直接复制
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      showSuccess(t('Link copied to clipboard'));
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleSocialShare = async (platform: 'x' | 'telegram' | 'discord') => {
    // 如果还不是公开链接，先转换为公开
    if (!isPublic) {
      setIsLoading(true);
      try {
        const response = await fetcher(postFetcherUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId,
            makePublic: true,
            messageIndex,
            searchQueryId: chatId,
          }),
        });
        const data = await response.json();
        setIsPublic(true);

        // 继续分享流程
        proceedWithShare(platform, shareUrl);
      } catch (error) {
        console.error('Failed to make chat public:', error);
        return;
      } finally {
        setIsLoading(false);
      }
    } else {
      // 已经是公开链接，直接分享
      proceedWithShare(platform, shareUrl);
    }
  };

  const proceedWithShare = (platform: 'x' | 'telegram' | 'discord', url: string) => {
    const encodedUrl = encodeURIComponent(url);
    const title = encodeURIComponent(chatTitle ?? '');

    const urls = {
      x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${title}`,
      telegram: `https://telegram.me/share/url?url=${encodedUrl}&text=${title}`,
      discord: url, // Discord requires manual paste
    };

    if (platform === 'discord') {
      navigator.clipboard.writeText(url);
      showSuccess(t('Link copied! Open Discord to share'));
    } else {
      window.open(urls[platform], '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 dark:bg-black/80 bg-white/80" onClick={onClose} />

      {/* Modal */}
      <Card
        ref={modalRef}
        className="relative rounded-[16px] w-[560px] animate-in fade-in zoom-in-95 duration-200 shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-divider">
          <h2 className="text-text-primary text-[16px] font-medium">
            {t('Share public link to chat')}
          </h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-black dark:hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Privacy Note */}
          {/* <p className="text-text-primary text-[14px] leading-[1.8]">
            {t(
              'Your name, custom instructions, and any messages you add after sharing stay private',
            )}
          </p> */}

          {/* Share Link */}
          <div className="flex items-center relative pt-7">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="w-full h-[50px]  bg-[#e9e9e9] dark:bg-[#2c2c2c] border border-gray-200 dark:border-transparent rounded-[50px] px-4 pr-32 text-text-primary text-[14px] outline-hidden focus:border-[#c91ae2] dark:focus:border-[#c91ae2] transition-colors"
            />
            <button
              onClick={handleCopy}
              disabled={isLoading}
              className="absolute right-[5px] bottom-[5px] h-[40px] flex items-center gap-1 px-3 bg-[#121212] dark:bg-[#FAFAFA] hover:opacity-90 rounded-[20px] dark:text-[#050505] text-[#EFEFEF] text-[14px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Link size={16} />
              {isLoading ? t('Creating...') : isCopied ? t('Copied!') : t('Copy link')}
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-border-divider" />

          {/* Social Share */}
          <div className="space-y-5">
            <h3 className="text-text-primary text-[14px] font-semibold text-center">
              {t('Share to')}
            </h3>
            <div className="flex justify-center gap-[60px]">
              {/* X/Twitter */}
              <Share2X query={(query || chatTitle)!} chatId={chatId} shareUrl={shareUrl} />

              {/* Telegram */}
              <button
                onClick={() => handleSocialShare('telegram')}
                className="flex flex-col items-center gap-2 group"
                disabled={isLoading}
              >
                <div className="w-[44px] h-[44px] bg-bg-main rounded-full flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-[#3d3d3d] transition-colors">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16.64 8.8C16.49 10.38 15.84 14.22 15.51 15.99C15.37 16.74 15.09 16.99 14.83 17.02C14.25 17.07 13.81 16.64 13.25 16.27C12.37 15.69 11.87 15.33 11.02 14.77C10.03 14.12 10.67 13.76 11.24 13.18C11.39 13.03 13.95 10.7 14 10.49C14.0069 10.4582 14.006 10.4261 13.9973 10.3953C13.9886 10.3645 13.9724 10.3361 13.95 10.3123C13.9276 10.2885 13.8997 10.27 13.8686 10.2585C13.8374 10.247 13.804 10.2427 13.77 10.246C13.69 10.254 12.29 11.158 9.55 13.1C9.13 13.39 8.75 13.54 8.41 13.53C8.04 13.52 7.33 13.33 6.8 13.17C6.14 12.97 5.62 12.87 5.67 12.49C5.7 12.29 5.97 12.09 6.5 11.87C9.42 10.6 11.39 9.77 12.42 9.38C15.38 8.23 15.88 8.05 16.21 8.05C16.28 8.05 16.44 8.07 16.54 8.15C16.62 8.22 16.65 8.31 16.66 8.38C16.65 8.43 16.67 8.58 16.64 8.8Z"
                      fill="currentColor"
                      className="text-text-primary"
                    />
                  </svg>
                </div>
                <span className="text-text-secondary text-[11px]">Telegram</span>
              </button>

              {/* Discord */}
              <button
                onClick={() => handleSocialShare('discord')}
                className="flex flex-col items-center gap-2 group"
                disabled={isLoading}
              >
                <div className="w-[44px] h-[44px]  bg-bg-main rounded-full flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-[#3d3d3d] transition-colors">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4C14.89 4 14.78 4.05 14.73 4.15C14.55 4.49 14.35 4.94 14.21 5.28C12.64 5.04 11.09 5.04 9.56 5.28C9.42 4.93 9.21 4.49 9.03 4.15C8.98 4.05 8.87 4 8.76 4C7.27 4.26 5.82 4.71 4.49 5.33C4.44 5.35 4.4 5.38 4.37 5.42C1.66 9.43 0.93 13.34 1.3 17.19C1.31 17.21 1.33 17.23 1.35 17.24C3.12 18.59 4.84 19.44 6.53 20.02C6.64 20.05 6.76 20 6.82 19.91C7.22 19.37 7.57 18.79 7.87 18.18C7.93 18.05 7.87 17.91 7.74 17.86C7.17 17.65 6.63 17.39 6.11 17.09C5.97 17.01 5.96 16.82 6.09 16.73C6.2 16.65 6.31 16.56 6.41 16.47C6.47 16.42 6.55 16.41 6.62 16.44C10.02 18.01 13.7 18.01 17.07 16.44C17.14 16.41 17.22 16.42 17.28 16.48C17.38 16.57 17.49 16.65 17.6 16.73C17.73 16.82 17.72 17.01 17.58 17.09C17.06 17.4 16.51 17.65 15.95 17.86C15.82 17.91 15.77 18.06 15.82 18.18C16.13 18.79 16.48 19.36 16.87 19.91C16.93 20 17.05 20.05 17.16 20.02C18.86 19.44 20.58 18.59 22.35 17.24C22.37 17.23 22.39 17.2 22.4 17.19C22.83 12.75 21.68 8.87 19.32 5.42C19.3 5.38 19.26 5.35 19.21 5.33H19.27ZM8.52 14.91C7.49 14.91 6.63 13.96 6.63 12.79C6.63 11.62 7.47 10.67 8.52 10.67C9.58 10.67 10.43 11.63 10.41 12.79C10.41 13.96 9.57 14.91 8.52 14.91ZM15.49 14.91C14.46 14.91 13.6 13.96 13.6 12.79C13.6 11.62 14.44 10.67 15.49 10.67C16.55 10.67 17.4 11.63 17.38 12.79C17.38 13.96 16.55 14.91 15.49 14.91Z"
                      fill="currentColor"
                      className="text-text-primary"
                    />
                  </svg>
                </div>
                <span className="text-text-secondary text-[11px]">Discord</span>
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
